import { takeLatest, takeEvery, put, call } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { IEmailTemplate } from '../../types/emailTemplate/emailTemplate.types';
import {
  GET_EMAIL_TEMPLATES_REQUEST,
  GET_EMAIL_TEMPLATE_REQUEST,
  CREATE_EMAIL_TEMPLATE_REQUEST,
  UPDATE_EMAIL_TEMPLATE_REQUEST,
  DELETE_EMAIL_TEMPLATE_REQUEST,
  CHECK_LINKS_REQUEST,
  GetEmailTemplatesRequestAction,
  GetEmailTemplateRequestAction,
  CreateEmailTemplateRequestAction,
  UpdateEmailTemplateRequestAction,
  DeleteEmailTemplateRequestAction,
  CheckLinksRequestAction,
} from './emailTemplates.types';
import {
  getEmailTemplatesSuccess,
  getEmailTemplatesFailure,
  getEmailTemplateSuccess,
  getEmailTemplateFailure,
  createEmailTemplateSuccess,
  createEmailTemplateFailure,
  updateEmailTemplateSuccess,
  updateEmailTemplateFailure,
  deleteEmailTemplateSuccess,
  deleteEmailTemplateFailure,
  checkLinksSuccess,
  checkLinksFailure,
} from './emailTemplates.action';
import * as emailTemplateService from '../../services/emailTemplateService/emailTemplateService';

// Worker Sagas
function* getEmailTemplatesWorker(
  action: GetEmailTemplatesRequestAction
): SagaIterator {
  try {
    const response = yield call(
      emailTemplateService.getEmailTemplates,
      action.payload
    );
    const templates = response?.data ?? response ?? [];
    yield put(getEmailTemplatesSuccess(templates));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch email templates';
    yield put(getEmailTemplatesFailure(errorMessage));
  }
}
function* getEmailTemplateWorker(
  action: GetEmailTemplateRequestAction
): SagaIterator {
  const templateId = action.payload;
  try {
    const template: IEmailTemplate | null = yield call(
      emailTemplateService.getEmailTemplateById,
      templateId
    );
    yield put(getEmailTemplateSuccess(template));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch email template';
    yield put(getEmailTemplateFailure(templateId, errorMessage));
  }
}

function* createEmailTemplateWorker(
  action: CreateEmailTemplateRequestAction
): SagaIterator {
  try {
    const template: IEmailTemplate = yield call(
      emailTemplateService.createEmailTemplate,
      action.payload
    );
    yield put(createEmailTemplateSuccess(template));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to create email template';
    yield put(createEmailTemplateFailure(errorMessage));
  }
}

function* updateEmailTemplateWorker(
  action: UpdateEmailTemplateRequestAction
): SagaIterator {
  try {
    const template: IEmailTemplate = yield call(
      emailTemplateService.updateEmailTemplate,
      action.payload.id,
      action.payload.data
    );
    yield put(updateEmailTemplateSuccess(template));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to update email template';
    yield put(updateEmailTemplateFailure(errorMessage));
  }
}

function* deleteEmailTemplateWorker(
  action: DeleteEmailTemplateRequestAction
): SagaIterator {
  try {
    yield call(emailTemplateService.deleteEmailTemplate, action.payload);
    yield put(deleteEmailTemplateSuccess(action.payload));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to delete email template';
    yield put(deleteEmailTemplateFailure(errorMessage));
  }
}

function* checkLinksWorker(action: CheckLinksRequestAction): SagaIterator {
  try {
    const data = yield call(emailTemplateService.checkLinks, action.payload);
    yield put(checkLinksSuccess(data));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || error.message || 'Failed to check links';
    yield put(checkLinksFailure(errorMessage));
  }
}

// Root Saga
export function* emailTemplatesSaga(): SagaIterator {
  yield takeLatest(GET_EMAIL_TEMPLATES_REQUEST, getEmailTemplatesWorker);
  yield takeEvery(GET_EMAIL_TEMPLATE_REQUEST, getEmailTemplateWorker);
  yield takeLatest(CREATE_EMAIL_TEMPLATE_REQUEST, createEmailTemplateWorker);
  yield takeLatest(UPDATE_EMAIL_TEMPLATE_REQUEST, updateEmailTemplateWorker);
  yield takeLatest(DELETE_EMAIL_TEMPLATE_REQUEST, deleteEmailTemplateWorker);
  yield takeLatest(CHECK_LINKS_REQUEST, checkLinksWorker);
}
